import { PageContainer, PageHeader, Skeleton } from '@ccp/ui';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AppErrorState } from '../../app/error-state.js';
import { useAgentBoard } from '../../features/supervision/api.js';
import { AgentBoardTable } from '../../features/supervision/components/agent-board.js';
import { SuperviseTabs } from '../../features/supervision/components/supervise-tabs.js';
import { useBoardTopics } from '../../features/supervision/use-board-topics.js';

export const Route = createFileRoute('/supervise/agents')({ component: AgentBoardScreen });

/**
 * The agent board.
 *
 * It watches the `team:<id>` topic of every team on it, and reads itself back at the dashboard
 * staleness budget. The teams come from the board the API answered with — which is already filtered
 * to this supervisor's scope — rather than from the permissions in the browser, so a screen cannot
 * ask to watch a team the server would not show it.
 */
function AgentBoardScreen() {
  const { t } = useTranslation('supervision');
  const board = useAgentBoard();
  const items = board.data?.items;

  useBoardTopics(
    'team',
    useMemo(() => [...new Set((items ?? []).flatMap((row) => row.teamIds))], [items]),
  );

  return (
    <PageContainer width="full">
      {/* The area is the heading and its two views are tabs under it. It was the other way round —
          two plain links and then a heading repeating whichever was open — and nothing said
          which of the two links was the current one (M5.5-01). */}
      <PageHeader title={t('title')} />
      <div className="flex flex-col gap-(--space-sm)">
        <SuperviseTabs />
        <p className="text-(length:--font-size-body) text-(--color-text-secondary)">
          {t('agents.description')}
        </p>
      </div>

      {board.isError ? (
        <AppErrorState error={board.error} resetErrorBoundary={() => void board.refetch()} />
      ) : board.isPending ? (
        <Skeleton className="h-40 w-full" label={t('agents.heading')} />
      ) : (
        <div className="flex flex-col gap-(--space-sm)">
          <AgentBoardTable board={board.data} />
          <p className="text-(length:--font-size-caption) text-(--color-text-muted)">
            {board.data.tenantWide ? t('scope.tenantWide') : t('scope.narrow')}
          </p>
        </div>
      )}
    </PageContainer>
  );
}
